import type { PeakInfo } from '@mimir/common/dto/pollingDtos';
import { PeaksFetcher } from './peaksFetcher';
import type { PeaksFetcherOps } from './peaksFetcherOps';
import type { BasicFetch } from '@mimir/common/types/basicFetch';
import type { PeaksDelta, PeaksDeltaDto, SingleTrackPeaks, SingleTrackPeaksDto } from '@mimir/common/dto/peaksDto';
import type { Logger } from '@mimir/common/types/logger';
import { fetchOrThrow } from '@mimir/common/io/fetchOrThrow';
import { readWholeStream } from '@mimir/common/util/readableStream';

export function makePeaksFetcher(
  services: {
    logger: Logger,
    fetch: BasicFetch,
    decodeBase64: (base64: string) => Uint8Array,
    fetchSingleTrackPeaksDto: (audioTrackIndex: number) => Promise<SingleTrackPeaksDto>,
    fetchPeaksDeltaDto: (peakOffset: number) => Promise<PeaksDeltaDto>,
  },
  peakInfo: PeakInfo,
): PeaksFetcherOps {

  const fetchSingleTrackPeaks = async (audioTrackIndex: number) => {
    const dto = await services.fetchSingleTrackPeaksDto(audioTrackIndex);
    const result: SingleTrackPeaks = {
      peaksPerSeconds: dto.peaksPerSeconds,
      maybeMoreContent: dto.maybeMoreContent,
      peaks: services.decodeBase64(dto.base64Data),
    };
    return result;
  };

  const fetchSingleTrackPeakBytes = async (url: string) => {
    const response = await fetchOrThrow(services.logger, services.fetch, url);
    const data = response.body ? await readWholeStream(response.body) : null;
    if (!data) {
      throw new Error(`Audio channel: ${url} failed to download`);
    }
    return data;
  };

  const fetchPeaksDelta = async (peakOffset: number) => {
    const dto = await services.fetchPeaksDeltaDto(peakOffset);
    const delta: PeaksDelta = {
      peaksPerSeconds: dto.peaksPerSeconds,
      truncated: dto.truncated,
      peaksPerChannel: dto.base64Data.map(base64 => services.decodeBase64(base64)),
    };
    return delta;
  };

  return new PeaksFetcher(
    peakInfo,
    fetchSingleTrackPeaks,
    fetchSingleTrackPeakBytes,
    fetchPeaksDelta,
  );
}
